// =============================================================================
// src/data/ai-surfaces/reputation-surfaces.ts — Superfícies de IA da Reputação.
//
// Takeover #reputationTakeover (src/features/reputation/**). Campos ancorados em
//   buildReputationPatch + buildCopilotOverlay.irm (src/data/sentinel-overlay.ts).
// =============================================================================

import type { AiSurfaceEntry } from '../ai-surface-types';

export const REPUTATION_AI_SURFACES: readonly AiSurfaceEntry[] = [
  {
    id: 'reputacao.index-gauge',
    label: 'Índice de reputação (gauge)',
    domain: 'reputacao',
    location: 'Takeover Reputação · gauge + faixa',
    renderRef: 'src/features/reputation/reputation-charts.ts#drawReputationGauge',
    aiKind: 'aggregate',
    backendLayer: 'camada-3',
    backendSource: { kind: 'hasura-view', name: 'monitoring.v_reputation_summary' },
    status: 'live',
    inputs: ['sentimento ponderado', 'presença', 'protagonismo'],
    fields: [
      { uiField: 'rep.index', backendField: 'reputation_index', type: 'number' },
      { uiField: 'rep.band', backendField: 'reputation_index', type: 'enum', transform: 'reputationBand (faixas)' },
      { uiField: 'rep.mentions24h', backendField: 'mentions_24h', type: 'number' },
    ],
  },
  {
    id: 'reputacao.trend',
    label: 'Evolução do índice',
    domain: 'reputacao',
    location: 'Takeover Reputação · linha de tendência',
    renderRef: 'src/features/reputation/reputation-trend.ts#drawReputationTrend',
    aiKind: 'forecast',
    backendLayer: 'camada-3',
    backendSource: { kind: 'hasura-view', name: 'monitoring.v_reputation_trend' },
    status: 'live',
    inputs: ['série histórica do índice'],
    fields: [
      { uiField: 'rep.trend[].value', backendField: 'reputation_index', type: 'number' },
      { uiField: 'rep.trend[].label', backendField: 'ts', type: 'iso-datetime', transform: 'formatTrendLabel' },
    ],
  },
  {
    id: 'reputacao.subdims',
    label: 'Subdimensões (Percepção/Presença/Protagonismo)',
    domain: 'reputacao',
    location: 'Takeover Reputação · 3 subdimensões',
    renderRef: 'src/features/reputation/index.ts#renderMarkup',
    aiKind: 'aggregate',
    backendLayer: 'camada-3',
    backendSource: { kind: 'hasura-view', name: 'monitoring.v_reputation_summary' },
    status: 'live',
    inputs: ['NRS', 'SOV', 'BPS'],
    fields: [
      { uiField: 'rep.percepcao', backendField: 'nrs', type: 'number' },
      { uiField: 'rep.presenca', backendField: 'sov', type: 'number' },
      { uiField: 'rep.protagonismo', backendField: 'bps', type: 'number' },
    ],
  },
  {
    id: 'reputacao.sov-donut',
    label: 'Share of Voice (donut)',
    domain: 'reputacao',
    location: 'Takeover Reputação · donut SOV',
    renderRef: 'src/features/reputation/reputation-charts.ts#drawReputationDonut',
    aiKind: 'aggregate',
    backendLayer: 'camada-3',
    backendSource: { kind: 'hasura-view', name: 'monitoring.v_reputation_summary' },
    status: 'partial',
    inputs: ['share of voice do cliente', 'split por concorrente'],
    fields: [{ uiField: 'rep.sov', backendField: 'sov', type: 'percent' }],
  },
  {
    id: 'reputacao.canais',
    label: 'Composição por canal',
    domain: 'reputacao',
    location: 'Takeover Reputação · barras por canal',
    renderRef: 'src/features/reputation/reputation-charts.ts#drawReputationChannels',
    aiKind: 'sentiment',
    backendLayer: 'camada-2',
    backendSource: { kind: 'hasura-view', name: 'monitoring.v_feed_recent' },
    status: 'mock',
    inputs: ['menções agrupadas por plataforma + sentimento'],
    fields: [
      { uiField: 'rep.canal[].total', backendField: 'platform (group by)', type: 'number' },
    ],
  },
  {
    id: 'reputacao.mencoes-negativas',
    label: 'Risco de menções negativas',
    domain: 'reputacao',
    location: 'Takeover Reputação · séries de negativas',
    renderRef: 'src/features/reputation/reputation-charts.ts#drawReputationNeg',
    aiKind: 'sentiment',
    backendLayer: 'camada-2',
    backendSource: { kind: 'hasura-table', name: 'monitoring.content_analysis' },
    status: 'mock',
    inputs: ['contagem de menções negativas por figura ao longo do tempo'],
    fields: [
      { uiField: 'rep.neg[].count', backendField: 'sentiment_score<0 (count)', type: 'number' },
    ],
  },
  {
    id: 'reputacao.ranking-segmento',
    label: 'Ranking vs segmento',
    domain: 'reputacao',
    location: 'Takeover Reputação · ranking + média do segmento',
    renderRef: 'src/features/reputation/reputation-charts.ts#drawReputationRank',
    aiKind: 'aggregate',
    backendLayer: 'camada-3',
    backendSource: { kind: 'hasura-view', name: 'monitoring.v_reputation_summary' },
    status: 'mock',
    inputs: ['índice de cada cliente do segmento'],
    fields: [
      { uiField: 'rep.rank[].index', backendField: 'reputation_index (por cliente)', type: 'number' },
    ],
  },
  {
    id: 'reputacao.duelo-1v1',
    label: 'Duelo 1×1',
    domain: 'reputacao',
    location: 'Takeover Reputação · comparação 4 dimensões',
    renderRef: 'src/features/reputation/reputation-duel.ts#renderReputationDuel',
    aiKind: 'aggregate',
    backendLayer: 'camada-3',
    backendSource: { kind: 'hasura-view', name: 'monitoring.v_reputation_summary' },
    status: 'mock',
    inputs: ['índice/NRS/SOV/BPS de dois clientes'],
    fields: [
      { uiField: 'duel.index', backendField: 'reputation_index', type: 'number' },
      { uiField: 'duel.nrs', backendField: 'nrs', type: 'number' },
      { uiField: 'duel.sov', backendField: 'sov', type: 'number' },
      { uiField: 'duel.bps', backendField: 'bps', type: 'number' },
    ],
  },
  {
    id: 'reputacao.acoes-stakeholder',
    label: 'Ações por stakeholder',
    domain: 'reputacao',
    location: 'Takeover Reputação · cards Eleitorado/Aliados/Imprensa/Institucionais',
    renderRef: 'src/features/reputation/index.ts#renderMarkup',
    aiKind: 'recommendation',
    backendLayer: 'camada-3',
    backendSource: { kind: 'hasura-table', name: 'gold.ai_action_recommendation' },
    status: 'mock',
    inputs: ['risco/oportunidade por público', 'ação recomendada'],
    fields: [
      { uiField: 'stake.acao', backendField: 'recommended_actions[].text', type: 'string' },
      { uiField: 'stake.tipo', backendField: 'action_type', type: 'enum' },
    ],
  },
  {
    id: 'reputacao.alerta-tempo-real',
    label: 'Alerta em tempo real',
    domain: 'reputacao',
    location: 'Takeover Reputação · bolha de alerta (WhatsApp)',
    renderRef: 'src/features/reputation/index.ts#renderMarkup',
    aiKind: 'recommendation',
    backendLayer: 'camada-3',
    backendSource: { kind: 'hasura-table', name: 'monitoring.alert' },
    status: 'mock',
    inputs: ['severidade do alerta', 'impacto quantificado'],
    fields: [
      { uiField: 'alerta.titulo', backendField: 'title', type: 'string' },
      { uiField: 'alerta.severidade', backendField: 'severity', type: 'enum' },
    ],
  },
];
